import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ReloadLocateScrollTarget,
  SelectedRowsContextType,
} from '../../types/context';
import { useDatasetContext } from '../../hooks/context/useDatasetContext';
import { useSlaveDatasetContext } from '../../hooks/context/useSlaveDatasetContext';
import { useMasterGridMultiselectModeChecker } from '../../hooks/useMasterGridMultiselectModeChecker';
import { useSlaveGridMultiselectModeChecker } from '../../hooks/useSlaveGridMultiselectModeChecker';
import { useLocation } from 'react-router';
import useRoutes from '../../hooks/useRoutes';
import { useSelectedRowsBatchUpdate } from '../../hooks/useSelectedRowsBatchUpdate';
import { useRemoveRowsAction } from '../../hooks/responseActions/datasets/useRemoveRowsAction';
import { useReloadAction } from '../../hooks/responseActions/datasets/useReloadAction';
import { remapSelectedRowsToData } from '../../utils/remapSelectedRowsToData';
import { canLocateSelection } from '../../utils/canLocateSelection';
import { locateSelectionAfterReload } from '../../utils/locateSelectionAfterReload';
import { getRowAfterRemoval } from '../../utils/getRowAfterRemoval';
import { getSubsetAndItemParams } from '../../utils/getSubsetAndItemParams';
import { useSelectedRowsChangedCachedQueryGuard } from '../../hooks/useSelectedRowsChangedCachedQueryGuard';
import { getDataKey } from '../../utils/getDataKey';
import { GlobalLocationStateType } from '../../types/types';
import { useSelectedRowsMasterHandlers } from './hooks/useSelectedRowsMasterHandlers';
import { useSelectedRowsSlaveHandlers } from './hooks/useSelectedRowsSlaveHandlers';
import { useSelectedRowsReferenceHandlers } from './hooks/useSelectedRowsReferenceHandlers';

const defaultValues: SelectedRowsContextType = {
  isProvided: false,
  expandedRow: null,
  selectedRows: [],
  selectedSlaveRows: [],
  selectedReferenceRows: [],
  selectAllChecked: false,
  selectAllSlaveChecked: false,
  selectAllReferenceChecked: false,
  selectedVirtualRows: 0,
  selectedSlaveVirtualRows: 0,
  selectedReferenceVirtualRows: 0,
  deselectedRowIds: new Set<string>(),
  deselectedSlaveRowIds: new Set<string>(),
  deselectedReferenceRowIds: new Set<string>(),
  reloadLocateScrollTargetRef: { current: null },
  slaveReloadLocateScrollTargetRef: { current: null },
  reloadLocateInProgress: false,
  slaveReloadLocateInProgress: false,
  setReloadLocateInProgress: () => {
    throw new Error('setReloadLocateInProgress must be implemented');
  },
  setSlaveReloadLocateInProgress: () => {
    throw new Error('setSlaveReloadLocateInProgress must be implemented');
  },
  setExpandedRow: () => {
    throw new Error('setExpandedRow must be implemented');
  },
  setSelectedRows: () => {
    throw new Error('setSelectedRows must be implemented');
  },
  setSelectedSlaveRows: () => {
    throw new Error('setSelectedSlaveRows must be implemented');
  },
  setSelectedReferenceRows: () => {
    throw new Error('setSelectedReferenceRows must be implemented');
  },
  processSelectedRowsChange: () => {
    throw new Error('processSelectedRowsChange must be implemented');
  },
  processSelectAllChange: () => {
    throw new Error('processSelectAllChange must be implemented');
  },
  processSelectedSlaveRowsChange: () => {
    throw new Error('processSelectedSlaveRowsChange must be implemented');
  },
  processSelectAllSlaveChange: () => {
    throw new Error('processSelectAllSlaveChange must be implemented');
  },
  processSelectedReferenceRowsChange: () => {
    throw new Error('processSelectedReferenceRowsChange must be implemented');
  },
  processSelectAllReferenceChange: () => {
    throw new Error('processSelectAllReferenceChange must be implemented');
  },
  setSelectedVirtualRows: () => {
    throw new Error('setSelectedVirtualRows must be implemented');
  },
  setSelectedSlaveVirtualRows: () => {
    throw new Error('setSelectedSlaveVirtualRows must be implemented');
  },
  setSelectedReferenceVirtualRows: () => {
    throw new Error('setSelectedReferenceVirtualRows must be implemented');
  },
  setSelectAllSlaveChecked: () => {
    throw new Error('setSelectAllSlaveChecked must be implemented');
  },
  setSelectAllReferenceChecked: () => {
    throw new Error('setSelectAllReferenceChecked must be implemented');
  },
  resetSelectedRows: () => {
    throw new Error('resetSelectedRows must be implemented');
  },
  resetSlaveSelectedRows: () => {
    throw new Error('resetSlaveSelectedRows must be implemented');
  },
  resetReferenceSelectedRows: () => {
    throw new Error('resetReferenceSelectedRows must be implemented');
  },
};

export const SelectedRowsContext =
  createContext<SelectedRowsContextType>(defaultValues);

const SelectedRowsContextProvider = ({ children }: { children: ReactNode }) => {
  const [selectedRows, setSelectedRows] = useState<any[]>(
    defaultValues.selectedRows
  );
  const [selectedVirtualRows, setSelectedVirtualRows] = useState<number>(0);
  // Rows the user unchecked while select-all was active. Select-all only ever
  // knows a virtual row *count*, so without these ids a re-ordering refetch
  // cannot tell which rows are meant to stay unchecked.
  const [deselectedRowIds, setDeselectedRowIds] = useState<Set<string>>(
    new Set<string>()
  );
  const [selectedSlaveRows, setSelectedSlaveRows] = useState<any[]>(
    defaultValues.selectedSlaveRows
  );
  const [selectedSlaveVirtualRows, setSelectedSlaveVirtualRows] =
    useState<number>(0);
  const [deselectedSlaveRowIds, setDeselectedSlaveRowIds] = useState<
    Set<string>
  >(new Set<string>());
  const [expandedRow, setExpandedRow] = useState<any>(
    defaultValues.expandedRow
  );
  const [selectAllChecked, setSelectAllChecked] = useState<boolean>(
    defaultValues.selectAllChecked
  );
  const [selectAllSlaveChecked, setSelectAllSlaveChecked] = useState<boolean>(
    defaultValues.selectAllSlaveChecked
  );
  // Reference grid state — data lives in FullscreenReference, so the
  // handlers below take it as a parameter (same pattern as slave).
  const [selectedReferenceRows, setSelectedReferenceRows] = useState<any[]>(
    defaultValues.selectedReferenceRows
  );
  const [selectedReferenceVirtualRows, setSelectedReferenceVirtualRows] =
    useState<number>(0);
  const [selectAllReferenceChecked, setSelectAllReferenceChecked] =
    useState<boolean>(defaultValues.selectAllReferenceChecked);
  const [deselectedReferenceRowIds, setDeselectedReferenceRowIds] = useState<
    Set<string>
  >(new Set<string>());

  const routes = useRoutes();
  const { pathname } = useLocation<GlobalLocationStateType>();
  const {
    datasetId,
    subsetId,
    datasetData,
    cachedQueryIdRef,
    data,
    dataset,
    removeRowsLocally,
    wasRowJustRemoved,
    fetchDatasetData,
    locateAndFetchById,
    locateAndFetchFarthestByIds,
  } = useDatasetContext();
  // Slave data lives in SlaveDatasetContext (a parent provider). This context
  // owns the slave selection state, so it also hosts the slave grid's selection
  // effects + the subset removeRows cleanup.
  const {
    data: slaveData,
    dataset: slaveDataset,
    datasetData: slaveDatasetData,
    cachedQueryId: slaveCachedQueryId,
    dataKey: slaveDataKey,
    subsetId: slaveSubsetId,
    expandedRow: slaveExpandedRow,
    setExpandedRow: setSlaveExpandedRow,
    removeRowsLocally: removeSlaveRowsLocally,
    fetchDatasetData: fetchSlaveDatasetData,
    locateAndFetchById: locateSlaveAndFetchById,
    locateAndFetchFarthestByIds: locateSlaveAndFetchFarthestByIds,
  } = useSlaveDatasetContext();
  const dataRef = useRef<any[]>(data ?? []);
  // Same primereact caveat as dataRef below: the checkbox handler keeps the
  // function it was first given, so the exclusion sets are mirrored into refs
  // and every write goes through these updaters to keep both in step.
  const deselectedRowIdsRef = useRef<Set<string>>(deselectedRowIds);
  const deselectedSlaveRowIdsRef = useRef<Set<string>>(deselectedSlaveRowIds);
  const deselectedReferenceRowIdsRef = useRef<Set<string>>(
    deselectedReferenceRowIds
  );
  const updateDeselectedRowIds = useCallback((ids: Set<string>): void => {
    deselectedRowIdsRef.current = ids;
    setDeselectedRowIds(ids);
  }, []);
  const updateDeselectedSlaveRowIds = useCallback((ids: Set<string>): void => {
    deselectedSlaveRowIdsRef.current = ids;
    setDeselectedSlaveRowIds(ids);
  }, []);
  const updateDeselectedReferenceRowIds = useCallback(
    (ids: Set<string>): void => {
      deselectedReferenceRowIdsRef.current = ids;
      setDeselectedReferenceRowIds(ids);
    },
    []
  );
  const dataKey = getDataKey(dataset);
  // One-shot scroll targets armed by the reload locate flows below; consumed
  // (and cleared) by useGridScrollBehavior once the row is in loaded data.
  const reloadLocateScrollTargetRef = useRef<ReloadLocateScrollTarget | null>(
    null
  );
  const slaveReloadLocateScrollTargetRef =
    useRef<ReloadLocateScrollTarget | null>(null);
  // Reload locate flow running — grid shows loading, controls are disabled.
  const [reloadLocateInProgress, setReloadLocateInProgress] =
    useState<boolean>(false);
  const [slaveReloadLocateInProgress, setSlaveReloadLocateInProgress] =
    useState<boolean>(false);

  // Reload after a method control: refetch, then keep the expanded row visible.
  // The row may have dropped off the reloaded page, so if it isn't present we
  // probe for its real position and fetch that page (locate handles scroll +
  // highlight downstream via the refreshed data).
  useReloadAction(async () => {
    const key = expandedRow?.[dataKey];
    if (key === undefined || key === null) {
      // Reload fetches only the first page; checked rows can live further
      // down. If some are missing, load everything up to the farthest one and
      // scroll to it (locateSelectionAfterReload). The flag goes on BEFORE the
      // refetch — it suppresses the grid lazy-load trigger for the whole flow.
      const shouldLocateSelection = canLocateSelection(
        selectedRows,
        selectAllChecked,
        selectedVirtualRows,
        dataset?.idItem
      );
      if (shouldLocateSelection) {
        setReloadLocateInProgress(true);
      }

      const rows = await fetchDatasetData();

      if (!shouldLocateSelection) return;
      await locateSelectionAfterReload({
        rows,
        selectedRows,
        dataKey,
        locateFarthestByIds: locateAndFetchFarthestByIds,
        scrollTargetRef: reloadLocateScrollTargetRef,
        setInProgress: setReloadLocateInProgress,
      });
      return;
    }
    const rows = await fetchDatasetData();
    const present =
      Array.isArray(rows) &&
      rows.some((row) => String(row?.[dataKey]) === String(key));
    if (!present) {
      // `key` is the pre-removal expandedRow: this closure was registered before
      // the concurrent removeRows handler moved it on. In the detail that means
      // the record was just deleted, so locating it would only toast a lie.
      const { itemId } = getSubsetAndItemParams(pathname);
      if (itemId && wasRowJustRemoved(String(key))) return;
      locateAndFetchById(String(key), true);
    }
  });

  // Subset reload (scope 'subset'). Lives here, not in SlaveDatasetContext:
  // the registry keeps one handler per scope+name and the flow needs the
  // slave selection state, which only this context owns.
  useReloadAction(async () => {
    if (!slaveDataset) return;
    const key = slaveExpandedRow?.[slaveDataKey];
    if (key !== undefined && key !== null) {
      // Keep the expanded subset row visible — it may have dropped off the
      // reloaded page (locate handles scroll + highlight downstream).
      locateSlaveAndFetchById(String(key), true);
      return;
    }

    const shouldLocateSelection = canLocateSelection(
      selectedSlaveRows,
      selectAllSlaveChecked,
      selectedSlaveVirtualRows,
      slaveDataset?.idItem
    );
    if (shouldLocateSelection) {
      setSlaveReloadLocateInProgress(true);
    }

    const rows = await fetchSlaveDatasetData(slaveDataset);

    if (!shouldLocateSelection) return;
    await locateSelectionAfterReload({
      rows,
      selectedRows: selectedSlaveRows,
      dataKey: slaveDataKey,
      locateFarthestByIds: locateSlaveAndFetchFarthestByIds,
      scrollTargetRef: slaveReloadLocateScrollTargetRef,
      setInProgress: setSlaveReloadLocateInProgress,
    });
  }, 'subset');

  useRemoveRowsAction((ids) => {
    removeRowsLocally(ids);

    const idSet = new Set(ids.map(String));
    const remainingSelected = selectedRows.filter(
      (row) => !idSet.has(String(row[dataKey]))
    );
    if (remainingSelected.length !== selectedRows.length) {
      setSelectedRows(remainingSelected);
      if (remainingSelected.length === 0) {
        setSelectAllChecked(false);
        setSelectedVirtualRows(0);
        updateDeselectedRowIds(new Set<string>());
      }
    }
    if (expandedRow && idSet.has(String(expandedRow[dataKey]))) {
      // Clearing it would drop the detail into its deep-link fallback (first row
      // + "row not found"); move onto the row that takes the deleted one's place.
      const rowsInDetail = selectedRows.length ? selectedRows : (data ?? []);
      setExpandedRow(
        getRowAfterRemoval(rowsInDetail, ids, dataKey, expandedRow)
      );
    }
  });

  useRemoveRowsAction((ids) => {
    removeSlaveRowsLocally(ids);

    const idSet = new Set(ids.map(String));
    const remainingSelected = selectedSlaveRows.filter(
      (row) => !idSet.has(String(row[slaveDataKey]))
    );
    if (remainingSelected.length !== selectedSlaveRows.length) {
      setSelectedSlaveRows(remainingSelected);
      if (remainingSelected.length === 0) {
        setSelectAllSlaveChecked(false);
        setSelectedSlaveVirtualRows(0);
        updateDeselectedSlaveRowIds(new Set<string>());
      }
    }
    if (slaveExpandedRow && idSet.has(String(slaveExpandedRow[slaveDataKey]))) {
      setSlaveExpandedRow(null);
    }
  }, 'subset');

  useMasterGridMultiselectModeChecker(
    expandedRow,
    selectedRows,
    datasetId,
    setExpandedRow
  );

  useSelectedRowsChangedCachedQueryGuard(
    cachedQueryIdRef,
    datasetData,
    datasetId,
    () => {
      setSelectedRows([]);
      setSelectedVirtualRows(0);
      setSelectAllChecked(false);
      updateDeselectedRowIds(new Set<string>());
    }
  );

  useSelectedRowsBatchUpdate(
    cachedQueryIdRef,
    data,
    datasetData,
    selectAllChecked,
    deselectedRowIds,
    dataKey,
    selectedVirtualRows,
    setSelectedRows,
    setSelectedVirtualRows
  );

  // SLave guards
  useSlaveGridMultiselectModeChecker(
    selectedSlaveRows,
    slaveExpandedRow,
    setSlaveExpandedRow
  );

  useSelectedRowsChangedCachedQueryGuard(
    slaveCachedQueryId,
    slaveDatasetData,
    slaveSubsetId || '',
    () => {
      setSelectedSlaveRows([]);
      setSelectedSlaveVirtualRows(0);
      setSelectAllSlaveChecked(false);
      updateDeselectedSlaveRowIds(new Set<string>());
    }
  );

  useSelectedRowsBatchUpdate(
    slaveCachedQueryId,
    slaveData,
    slaveDatasetData,
    selectAllSlaveChecked,
    deselectedSlaveRowIds,
    slaveDataKey,
    selectedSlaveVirtualRows,
    setSelectedSlaveRows,
    setSelectedSlaveVirtualRows
  );

  const {
    processSelectedRowsChange,
    processSelectAllChange,
    resetSelectedRows,
  } = useSelectedRowsMasterHandlers({
    datasetData,
    dataKey,
    dataRef,
    selectedRows,
    selectAllChecked,
    setSelectAllChecked,
    setSelectedRows,
    setSelectedVirtualRows,
    deselectedRowIdsRef,
    updateDeselectedRowIds,
  });

  const {
    processSelectedSlaveRowsChange,
    processSelectAllSlaveChange,
    resetSlaveSelectedRows,
  } = useSelectedRowsSlaveHandlers({
    selectAllSlaveChecked,
    setSelectAllSlaveChecked,
    setSelectedSlaveRows,
    setSelectedSlaveVirtualRows,
    deselectedSlaveRowIdsRef,
    updateDeselectedSlaveRowIds,
  });

  const {
    processSelectedReferenceRowsChange,
    processSelectAllReferenceChange,
    resetReferenceSelectedRows,
  } = useSelectedRowsReferenceHandlers({
    selectAllReferenceChecked,
    setSelectAllReferenceChecked,
    setSelectedReferenceRows,
    setSelectedReferenceVirtualRows,
    deselectedReferenceRowIdsRef,
    updateDeselectedReferenceRowIds,
  });

  useEffect(() => {
    setSelectedRows([]);
    setSelectedVirtualRows(0);
    setExpandedRow(null);
    setSelectAllChecked(false);
    updateDeselectedRowIds(new Set<string>());
    // Leaving mid-locate: drop the scroll target and release the loading state.
    reloadLocateScrollTargetRef.current = null;
    setReloadLocateInProgress(false);
  }, [datasetId]);

  useEffect(() => {
    setSelectedSlaveRows([]);
    setSelectedSlaveVirtualRows(0);
    setSelectAllSlaveChecked(false);
    updateDeselectedSlaveRowIds(new Set<string>());
    // Leaving mid-locate: drop the scroll target and release the loading state.
    slaveReloadLocateScrollTargetRef.current = null;
    setSlaveReloadLocateInProgress(false);
  }, [subsetId]);

  useEffect(() => {
    if (expandedRow && pathname === routes.datasetList(datasetId ?? '')) {
      setExpandedRow(defaultValues.expandedRow);
    }
  }, [expandedRow, pathname]);

  // Update data ref when data changes due to bug in primereact on checkbox click handler
  // => not updating reference on function
  useEffect(() => {
    if (data) {
      dataRef.current = data;
    }
  }, [data]);

  // Swap selection onto the fresh row objects whenever grid data changes
  // (reload replace or lazy append) — see remapSelectedRowsToData. No-op
  // when references already match.
  useEffect(() => {
    if (!data || data.length === 0) return;
    setSelectedRows((previousSelectedRows) =>
      remapSelectedRowsToData(previousSelectedRows, data, dataKey)
    );
  }, [data, dataKey]);

  useEffect(() => {
    if (!slaveData || slaveData.length === 0) return;
    setSelectedSlaveRows((previousSelectedRows) =>
      remapSelectedRowsToData(previousSelectedRows, slaveData, slaveDataKey)
    );
  }, [slaveData, slaveDataKey]);

  const value: SelectedRowsContextType = useMemo(
    () => ({
      isProvided: true,
      expandedRow,
      selectedRows,
      selectAllChecked,
      selectAllSlaveChecked,
      selectAllReferenceChecked,
      selectedSlaveRows,
      selectedReferenceRows,
      selectedVirtualRows,
      selectedSlaveVirtualRows,
      selectedReferenceVirtualRows,
      deselectedRowIds,
      deselectedSlaveRowIds,
      deselectedReferenceRowIds,
      reloadLocateScrollTargetRef,
      slaveReloadLocateScrollTargetRef,
      reloadLocateInProgress,
      slaveReloadLocateInProgress,
      setReloadLocateInProgress,
      setSlaveReloadLocateInProgress,
      processSelectedRowsChange,
      processSelectAllChange,
      setSelectedRows,
      setExpandedRow,
      setSelectedSlaveRows,
      setSelectedReferenceRows,
      processSelectedSlaveRowsChange,
      processSelectAllSlaveChange,
      processSelectedReferenceRowsChange,
      processSelectAllReferenceChange,
      setSelectedVirtualRows,
      setSelectedSlaveVirtualRows,
      setSelectedReferenceVirtualRows,
      setSelectAllSlaveChecked,
      setSelectAllReferenceChecked,
      resetSelectedRows,
      resetSlaveSelectedRows,
      resetReferenceSelectedRows,
    }),
    [
      selectedRows,
      selectedVirtualRows,
      selectedSlaveVirtualRows,
      selectedReferenceVirtualRows,
      deselectedRowIds,
      deselectedSlaveRowIds,
      deselectedReferenceRowIds,
      reloadLocateInProgress,
      slaveReloadLocateInProgress,
      selectedSlaveRows,
      selectedReferenceRows,
      expandedRow,
      selectAllChecked,
      selectAllSlaveChecked,
      selectAllReferenceChecked,
      datasetData,
      data,
      setSelectedRows,
      setExpandedRow,
      processSelectedRowsChange,
      processSelectAllChange,
      setSelectedSlaveRows,
      setSelectedReferenceRows,
      processSelectedSlaveRowsChange,
      processSelectAllSlaveChange,
      processSelectedReferenceRowsChange,
      processSelectAllReferenceChange,
      setSelectedVirtualRows,
      setSelectedSlaveVirtualRows,
      setSelectedReferenceVirtualRows,
      setSelectAllSlaveChecked,
      setSelectAllReferenceChecked,
      resetSelectedRows,
      resetSlaveSelectedRows,
      resetReferenceSelectedRows,
    ]
  );

  return (
    <SelectedRowsContext.Provider value={value}>
      {children}
    </SelectedRowsContext.Provider>
  );
};

export default SelectedRowsContextProvider;
